import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Navigation, ChevronRight, Phone, Route, Home, Building2, Pill,
  Stethoscope, ArrowLeft, X, ListChecks, CalendarClock, Users, Info,
  RefreshCw, Smartphone, MonitorSmartphone, Lock, Video, ExternalLink, Clock3, AlertTriangle, ShieldCheck, Cross,
} from "lucide-react";
import { SafetyBanner } from "@/components/instapharm/SafetyBanner";
import { StatusPill } from "@/components/instapharm/StatusPill";
import { EstablishmentCard } from "@/components/instapharm/EstablishmentCard";

const CITY_COORDS = {
  "Hyères": [43.1205, 6.1286],
  "La Londe-les-Maures": [43.1383, 6.2347],
  "Sanary-sur-Mer": [43.1183, 5.8017],
  "Bandol": [43.1355, 5.7534],
  "Six-Fours-les-Plages": [43.0925, 5.8386],
  "La Garde": [43.1256, 6.0108],
};

const PROFILE_ICONS = { can_move: Navigation, cannot_move: Home, pharmacy: Pill };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F9F8F6]">
    <div className="max-w-md mx-auto min-h-screen bg-[#F9F8F6] relative shadow-[0_0_40px_rgba(0,0,0,0.05)]">
      <SafetyBanner />
      {children}
    </div>
  </div>
);

export default function InstapharmApp() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("home");
  const [profile, setProfile] = useState(null);
  const [coords, setCoords] = useState(null); // {lat, lng, label}
  const [cityChoice, setCityChoice] = useState("");
  const [establishments, setEstablishments] = useState([]);
  const [comparator, setComparator] = useState(null);
  const [listTab, setListTab] = useState("around");
  const [openNow, setOpenNow] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showGeoHelp, setShowGeoHelp] = useState(false);

  useEffect(() => {
    api.get("/instapharm/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async (c, prof, on) => {
    setLoading(true);
    try {
      const params = { openNow: on };
      if (c) { params.lat = c.lat; params.lng = c.lng; }
      const [l, comp] = await Promise.all([
        api.get("/instapharm/establishments", { params }),
        api.get("/instapharm/comparator", { params: { lat: c?.lat, lng: c?.lng, profile: prof } }),
      ]);
      setEstablishments(l.data.establishments);
      setComparator(comp.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  const goToList = (c) => { setCoords(c); setTypeFilter(null); setStep("list"); fetchData(c, profile, openNow); };

  const locateMe = async () => {
    if (!profile) { toast.error("Choisissez d'abord votre situation ci-dessus."); return; }
    if (!("geolocation" in navigator)) { toast.error("Géolocalisation indisponible sur cet appareil."); return; }
    // Si la position a déjà été bloquée, l'invite native ne réapparaît pas -> on guide vers les réglages.
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: "geolocation" });
        if (st.state === "denied") { setShowGeoHelp(true); return; }
      }
    } catch { /* Permissions API indisponible : on tente directement l'invite native */ }
    setGeoLoading(true);
    let done = false;
    const finish = () => { if (!done) { done = true; setGeoLoading(false); } };
    // Filet de sécurité si le navigateur ne rappelle jamais (iframe bloquée, etc.)
    const guard = setTimeout(() => {
      if (!done) { finish(); toast.error("Impossible d'obtenir votre position."); setShowGeoHelp(true); }
    }, 12000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(guard); finish();
        goToList({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Ma position" });
      },
      (error) => {
        clearTimeout(guard); finish();
        const code = error && error.code;
        const msg = {
          1: "Autorisation de localisation refusée. Voici comment l'activer.",
          2: "Position momentanément indisponible. Réessayez ou choisissez une ville.",
          3: "La localisation a pris trop de temps. Réessayez ou choisissez une ville.",
        }[code] || "Impossible d'obtenir votre position.";
        toast.error(msg);
        if (code === 1) setShowGeoHelp(true); // refus -> on guide vers les réglages
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const toggleOpenNow = (v) => { setOpenNow(v); fetchData(coords, profile, v); };

  const openFiche = async (e) => {
    try {
      const { data } = await api.get(`/instapharm/establishments/${e.id}`, { params: coords ? { lat: coords.lat, lng: coords.lng } : {} });
      setSelected(data);
    } catch { setSelected(e); }
  };

  if (!config) return <Shell><div className="p-6 text-slate-500">Chargement…</div></Shell>;

  // ---------- Écran d'accueil : profil + localisation ----------
  if (step === "home") {
    return (
      <Shell>
        <div className="px-5 py-7">
          <button data-testid="insta-home-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-xl bg-[#1E3A8A] grid place-items-center text-white"><Stethoscope className="h-5 w-5" /></div>
            <div>
              <div className="font-['Outfit'] font-bold text-lg leading-none text-slate-900">Services d'urgence</div>
              <div className="text-xs text-slate-500 mt-0.5">Une solution médicale ouverte, près de vous</div>
            </div>
          </div>

          <h1 className="font-['Outfit'] text-3xl font-bold tracking-tight leading-tight text-slate-900 mb-1.5">Trouvez une solution disponible aujourd'hui</h1>
          <p className="text-sm text-slate-600 mb-6">Choisissez votre situation. Nous n'établissons aucun diagnostic et ne vous demandons aucun symptôme.</p>

          <button
            data-testid="go-centres"
            onClick={() => navigate("/instapharm/centres")}
            className="w-full mb-7 p-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-left flex items-center justify-between active:scale-[0.98] transition-transform"
          >
            <span className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-white/20 grid place-items-center"><Cross className="h-5 w-5" /></span>
              <span>
                <span className="block font-['Outfit'] font-bold">Trouver un centre de soins</span>
                <span className="block text-sm text-white/85">Soins non programmés autour de vous</span>
              </span>
            </span>
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-3">Votre situation</div>
          <div className="space-y-3 mb-7">
            {config.profiles.map((p) => {
              const Icon = PROFILE_ICONS[p.id] || Building2;
              const active = profile === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`profile-select-${p.id}`}
                  onClick={() => setProfile(p.id)}
                  className={`w-full p-5 rounded-xl border-2 transition-colors duration-200 flex items-center justify-between group active:scale-[0.98] ${active ? "border-[#1E3A8A] bg-[#1E3A8A]/[0.04]" : "border-slate-100 bg-white hover:border-slate-300"}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-10 w-10 rounded-lg grid place-items-center ${active ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-500"}`}><Icon className="h-5 w-5" /></span>
                    <span className="font-medium text-slate-800">{p.label}</span>
                  </span>
                  <ChevronRight className={`h-5 w-5 ${active ? "text-[#1E3A8A]" : "text-slate-300"}`} />
                </button>
              );
            })}
          </div>

          <div className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-3">Où êtes-vous ?</div>
          <Button
            data-testid="use-geolocation-btn"
            onClick={locateMe}
            disabled={geoLoading}
            className="w-full h-14 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] gap-2 text-base"
          >
            <Navigation className="h-5 w-5" /> {geoLoading ? "Localisation…" : "Utiliser ma position"}
          </Button>
          <p className="text-xs text-slate-400 mt-2 text-center" data-testid="geo-help-hint">
            La localisation ne s'active pas ?{" "}
            <button type="button" onClick={locateMe} data-testid="geo-help-link" className="underline underline-offset-2 hover:text-slate-600">
              Autoriser la position
            </button>
          </p>
          <div className="flex items-center gap-3 my-4 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> ou choisissez une ville <div className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="city-list">
            {config.pilotCities.map((c) => {
              const active = cityChoice === c;
              return (
                <button
                  key={c}
                  data-testid={`city-chip-${c}`}
                  onClick={() => {
                    if (!profile) { toast.error("Choisissez d'abord votre situation."); return; }
                    setCityChoice(c);
                    const [lat, lng] = CITY_COORDS[c];
                    goToList({ lat, lng, label: c });
                  }}
                  className={`h-12 rounded-xl border text-sm font-medium transition-colors duration-200 active:scale-[0.97] ${active ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Votre position n'est utilisée que le temps de la recherche et n'est jamais conservée.</p>
          <button onClick={() => navigate("/instapharm/pro")} data-testid="goto-backoffice" className="mt-6 text-xs text-slate-400 underline underline-offset-2">Espace établissement</button>
          <div className="mt-2 text-xs text-slate-400">
            <button onClick={() => navigate("/instapharm/legal")} data-testid="goto-legal" className="underline underline-offset-2">Mentions légales · CGU · Confidentialité</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Services d'urgence est une plateforme neutre d'orientation. Aucun diagnostic, aucune prise en charge garantie.</p>
        </div>
        <GeoHelpDialog open={showGeoHelp} onClose={() => setShowGeoHelp(false)} onRetry={() => { setShowGeoHelp(false); locateMe(); }} />
      </Shell>
    );
  }

  // ---------- Liste « Autour de moi » / « Ouvert maintenant » + comparateur ----------
  const availableTypes = [...new Set(establishments.map((e) => e.type))];
  const shownList = typeFilter ? establishments.filter((e) => e.type === typeFilter) : establishments;
  return (
    <Shell>
      <div className="px-5 py-5 pb-24">
        <button data-testid="list-back" onClick={() => setStep("home")} className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>

        <div className="flex items-end justify-between mb-1">
          <h1 className="font-['Outfit'] text-2xl font-bold tracking-tight text-slate-900">{listTab === "around" ? "Autour de moi" : "Téléconsultation tout de suite"}</h1>
          <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> {coords?.label}</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">Classement neutre : ouverture, distance et disponibilité réelle. Aucune mise en avant payante.</p>

        {/* Onglets : lieux physiques vs téléconsultation immédiate */}
        <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl mb-5" data-testid="insta-tabs">
          {[["around", "Autour de moi"], ["teleconsult", "Téléconsultation tout de suite"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`insta-tab-${id}`}
              onClick={() => setListTab(id)}
              className={`h-10 rounded-lg text-[13px] font-semibold transition-colors duration-200 ${listTab === id ? "bg-white text-[#1E3A8A] shadow-sm" : "text-slate-500"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {listTab === "around" ? (
          <>
            {/* Comparateur */}
            {comparator && comparator.rows.length > 0 && (
              <div className="mb-5" data-testid="comparator">
                <div className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-2 flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Comparateur de solutions</div>
                <div className="grid grid-cols-1 gap-2">
                  {comparator.rows.map((r, i) => (
                    <button
                      key={i}
                      data-testid={`comparator-row-${i}`}
                      disabled={!r.establishmentId}
                      onClick={() => r.establishmentId && openFiche({ id: r.establishmentId })}
                      className="text-left bg-white rounded-xl border border-slate-200 p-3.5 flex items-center justify-between disabled:opacity-50 active:scale-[0.99] transition-transform"
                    >
                      <div>
                        <div className="font-medium text-sm text-slate-800 flex items-center gap-2">
                          {r.solutionLabel}
                          {comparator.recommendedIndex === i && <span className="text-[10px] font-bold uppercase tracking-wide bg-[#1E3A8A] text-white px-1.5 py-0.5 rounded">Le plus rapide probable</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.distanceKm != null ? `À ${r.distanceKm} km environ` : "Distance selon votre position"}
                        </div>
                      </div>
                      <StatusPill status={r.status} testid={`comparator-status-${i}`} />
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">{comparator.notice}</p>
              </div>
            )}

            {/* Filtre ouvert maintenant */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3 mb-4">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#1E3A8A]" /> Ouvert maintenant</span>
              <Switch data-testid="open-now-toggle" checked={openNow} onCheckedChange={toggleOpenNow} />
            </div>

            {/* Filtres par type */}
            {availableTypes.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scrollbar-none" data-testid="type-filters">
                <button
                  data-testid="type-filter-all"
                  onClick={() => setTypeFilter(null)}
                  className={`shrink-0 h-9 px-3.5 rounded-full text-[13px] font-semibold border transition-colors ${typeFilter === null ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}
                >
                  Tous ({establishments.length})
                </button>
                {availableTypes.map((t) => {
                  const n = establishments.filter((e) => e.type === t).length;
                  return (
                    <button
                      key={t}
                      data-testid={`type-filter-${t}`}
                      onClick={() => setTypeFilter(t)}
                      className={`shrink-0 h-9 px-3.5 rounded-full text-[13px] font-semibold border transition-colors ${typeFilter === t ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}
                    >
                      {config?.typeLabels?.[t] || t} ({n})
                    </button>
                  );
                })}
              </div>
            )}

            {loading ? (
              <div className="text-slate-500 text-sm py-8 text-center">Chargement…</div>
            ) : shownList.length === 0 ? (
              <div data-testid="empty-list" className="text-center py-10 text-slate-500 text-sm">Aucun établissement {openNow ? "ouvert " : ""}trouvé pour cette recherche.</div>
            ) : (
              <div className="space-y-3" aria-live="polite" data-testid="establishment-list">
                {shownList.map((e) => <EstablishmentCard key={e.id} e={e} onOpen={openFiche} />)}
              </div>
            )}
          </>
        ) : (
          <TeleconsultTab tc={config.teleconsultation} />
        )}
      </div>

      {/* Fiche du centre (bottom sheet) */}
      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent className="max-w-md mx-auto max-h-[92vh]">
          {selected && <FicheContent e={selected} onClose={() => setSelected(null)} />}
        </DrawerContent>
      </Drawer>
    </Shell>
  );
}

// ---------- Contenu de la fiche établissement ----------
function FicheContent({ e, onClose }) {
  const digits = (e.phone || "").replace(/[\s.\-]/g, "");
  const mapsQuery = encodeURIComponent(e.address || e.name);
  const hoursText = e.hours?.note || (Array.isArray(e.hours) ? e.hours.join(", ") : null);
  const Row = ({ icon: Icon, label, children }) => (
    <div className="flex gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <Icon className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-xs text-slate-400">{label}</div>
        <div className="text-sm text-slate-800">{children}</div>
      </div>
    </div>
  );
  return (
    <div className="overflow-y-auto px-5 pt-2 pb-6" data-testid="fiche-drawer">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] tracking-[0.1em] uppercase font-bold text-[#1E3A8A]">{e.typeLabel}</div>
          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 leading-tight mt-0.5">{e.name}</h2>
        </div>
        <button data-testid="fiche-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full bg-slate-100 text-slate-500 shrink-0"><X className="h-4 w-4" /></button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <StatusPill status={e.status} testid="fiche-status" />
        {e.waitMinutes != null && <span className="text-xs text-slate-500">~{e.waitMinutes} min d'attente</span>}
        {e.needsVerification && (
          <span data-testid="fiche-unverified" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Infos à confirmer
          </span>
        )}
      </div>
      {e.needsVerification && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          Le numéro et/ou les horaires de cet établissement n'ont pas encore été confirmés. Vérifiez auprès de l'établissement avant de vous déplacer, surtout en situation d'urgence.
        </p>
      )}
      {e.statusNote && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mb-4">{e.statusNote}</p>}

      <div className="bg-white rounded-xl border border-slate-200 px-4 mb-4">
        {e.address && <Row icon={MapPin} label="Adresse"><span data-testid="fiche-address">{e.address}</span></Row>}
        {e.phone && <Row icon={Phone} label="Téléphone"><span data-testid="fiche-phone">{e.phone}</span></Row>}
        {(e.type === "teleconsultation_domicile" || e.distanceKm != null) && (
          <Row icon={Navigation} label="Distance">{e.type === "teleconsultation_domicile" ? "À distance · sans déplacement" : `${e.distanceKm} km`}</Row>
        )}
        {hoursText && <Row icon={CalendarClock} label="Horaires"><span data-testid="fiche-hours">{hoursText}</span></Row>}
        {e.withAppointment != null && <Row icon={Info} label="Rendez-vous">{e.withAppointment ? "Sur rendez-vous" : "Sans rendez-vous"}</Row>}
        {e.callBefore != null && <Row icon={Phone} label="Appeler avant de venir">{e.callBefore ? "Oui, appeler avant" : "Non nécessaire"}</Row>}
        {e.acceptedPatients?.length > 0 && <Row icon={Users} label="Patients acceptés"><span data-testid="fiche-patients">{e.acceptedPatients.join(", ")}</span></Row>}
        {e.care?.length > 0 && <Row icon={Stethoscope} label="Soins disponibles"><span data-testid="fiche-care">{e.care.join(", ")}</span></Row>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {digits ? (
          <a data-testid="btn-call-center" href={`tel:${digits}`} className="h-14 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-medium grid place-items-center active:scale-[0.98] transition-transform">
            <span className="flex items-center gap-2"><Phone className="h-5 w-5" /> Appeler</span>
          </a>
        ) : (
          <button data-testid="btn-call-disabled" disabled className="h-14 rounded-xl bg-slate-100 text-slate-400 font-medium grid place-items-center cursor-not-allowed">
            <span className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4" /> Appel indisponible</span>
          </button>
        )}
        <a data-testid="btn-get-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`} target="_blank" rel="noopener noreferrer" className="h-14 rounded-xl border-2 border-[#1E3A8A] text-[#1E3A8A] font-medium grid place-items-center active:scale-[0.98] transition-transform">
          <span className="flex items-center gap-2"><Route className="h-5 w-5" /> Itinéraire</span>
        </a>
      </div>
      <p className="text-[11px] text-slate-400 mt-3 text-center">Services d'urgence oriente vers une solution : elle n'établit aucun diagnostic.</p>
      {(e.lastConfirmedAt || e.lastVerifiedAt) && (
        <div className="text-[11px] text-slate-400 mt-2 text-center flex items-center justify-center gap-1" data-testid="fiche-verified-at">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Dernière vérification : {new Date(e.lastConfirmedAt || e.lastVerifiedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-2 text-center" data-testid="fiche-source">
        {e.dataSource ? `Source : ${e.dataSource}. ` : ""}
        <a href={`mailto:contact@instapharm.fr?subject=Signalement%20-%20${encodeURIComponent(e.name)}`} className="underline underline-offset-2">Signaler une erreur</a>
      </div>
    </div>
  );
}

// ---------- Onglet « Téléconsultation tout de suite » ----------
function TeleconsultTab({ tc }) {
  const [pending, setPending] = useState(null); // provider en attente de confirmation
  if (!tc) return null;
  const confirmRedirect = () => {
    if (pending?.url) window.open(pending.url, "_blank", "noopener,noreferrer");
    setPending(null);
  };
  return (
    <div data-testid="teleconsult-tab">
      <div className="rounded-xl bg-[#1E3A8A]/[0.05] border border-[#1E3A8A]/15 p-4 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Video className="h-4 w-4 text-[#1E3A8A]" />
          <span className="text-sm font-semibold text-[#1E3A8A]">Consulter en vidéo, sans vous déplacer</span>
        </div>
        <p className="text-sm text-slate-600">{tc.subtitle}</p>
      </div>
      <div className="space-y-3">
        {tc.providers.map((p) => (
          <button
            key={p.slug}
            data-testid={`teleconsult-provider-${p.slug}`}
            onClick={() => setPending(p)}
            className="w-full text-left block bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-5 active:scale-[0.98]"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-['Outfit'] font-bold text-slate-900">{p.name}</span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A]">{p.cta} <ExternalLink className="h-4 w-4" /></span>
            </div>
            <p className="text-sm text-slate-500">{p.note}</p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <Clock3 className="h-3.5 w-3.5" /> {p.delay || "Médecins généralistes · France entière"}
            </div>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-4">{tc.notice}</p>

      {/* Avertissement avant redirection vers une plateforme externe */}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="redirect-notice-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-[#1E3A8A]" /> Vous allez être redirigé vers un service externe
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed pt-1">
              Services d'urgence n'est ni affilié ni partenaire de {pending?.name || "Doctolib, Qare"} ou des autres plateformes présentées, sauf mention contraire résultant d'un accord écrit.
              Les disponibilités, comptes utilisateurs et réservations sont entièrement gérés par la plateforme externe.
              Aucune donnée personnelle ou médicale n'est transmise lors de la redirection.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button data-testid="redirect-cancel" onClick={() => setPending(null)} className="h-12 rounded-xl border border-slate-300 text-slate-700 font-medium active:scale-[0.98] transition-transform">Annuler</button>
            <button data-testid="redirect-confirm" onClick={confirmRedirect} className="h-12 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              Continuer <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ---------- Fenêtre d'aide : autoriser la localisation ----------
function GeoHelpDialog({ open, onClose, onRetry }) {
  const Step = ({ icon: Icon, title, children }) => (
    <div className="flex gap-3">
      <div className="h-9 w-9 rounded-lg bg-[#1E3A8A]/[0.08] grid place-items-center text-[#1E3A8A] shrink-0"><Icon className="h-4 w-4" /></div>
      <div>
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="geo-help-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg">Autoriser la localisation</DialogTitle>
          <DialogDescription>
            Pour trouver les établissements les plus proches, autorisez l'accès à votre position. Elle n'est utilisée que le temps de la recherche.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Step icon={Lock} title="Au clic sur « Utiliser ma position »">
            Touchez <b>Autoriser</b> dans la fenêtre affichée par votre navigateur.
          </Step>
          <Step icon={Smartphone} title="Sur mobile (déjà refusé ?)">
            iPhone : Réglages → Confidentialité → Service de localisation → Safari → « Lorsque l'app est active ».<br />
            Android : Paramètres → Position (activée), puis Chrome → Paramètres du site → Localisation → Autorisé.
          </Step>
          <Step icon={MonitorSmartphone} title="Sur ordinateur">
            Cliquez sur l'icône <b>cadenas 🔒</b> à gauche de l'adresse → Localisation → <b>Autoriser</b>, puis rechargez.
          </Step>
          <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
            Astuce : si la page est ouverte dans un aperçu intégré, ouvrez-la dans un <b>onglet plein écran</b> — certains navigateurs y bloquent la localisation.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-1">
          <button data-testid="geo-help-close" onClick={onClose} className="h-12 rounded-xl border border-slate-300 text-slate-700 font-medium active:scale-[0.98] transition-transform">Fermer</button>
          <button data-testid="geo-help-retry" onClick={onRetry} className="h-12 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            <RefreshCw className="h-4 w-4" /> Réessayer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
